// Honolulu, "Waikiki dusk by Diamond Head".
// We stand on the sand of Waikiki Beach near Kuhio Beach, looking east-south-east along the shore at Diamond Head
// (Lēʻahi): the hotel towers of Waikiki line the curve of the beach on the left and fall away towards Kapahulu, the
// trees of Kapiʻolani Park and a few condos sit at the crater's foot, the Diamond Head lighthouse stands on its
// seaward point, and the open Pacific runs out on the right, where the last of the sunset glows on the horizon. The
// full moon rises over the crater.
// Back to front: a dithered dusk sky (indigo above, mauve, then a coral afterglow over the sea), stars and the
// evening star, the rising moon; trade-wind clouds drifting at two depths; Diamond Head with its ribbed western face
// catching the afterglow, the houses on its slopes and the lighthouse; the towers with their balconies, a rooftop
// sign for rent on the old beachfront hotel; the bay (rippled reflections, the moon path, the reef break with surfers
// in the lineup, an outrigger canoe and a sunset catamaran); the shore break on the sand; the board rack, the
// lifeguard tower, a canoe drawn up on the beach, tiki torches; a couple on a towel, a man playing ukulele under the
// palm, people walking the waterline; the leaning coconut palm in front.
//
// The loop's timeline (s):
//   always   shore waves every 8 s, reef sets every 20 s, the lighthouse every 6 s, torches, windows, clouds, palm
//   6-66     a couple strolls the waterline, right to left
//   84-140   an outrigger canoe paddles across the bay, left to right
//   40, 160  a surfer in the lineup catches a wave and rides it in (a splash as he kicks out)
//   74-118   a surfer walks in from the left with his board under his arm
//   96-150   a night flight climbs out over the sea
//   30-64, 150-184  a pair of fairy terns crosses the sky, leaving behind the hotels
//   120-206  the sunset catamaran sails home across the bay, right to left
//   128-182  a woman walks her dog along the water, right to left
//   200-234  a jogger, left to right
//   every 30 s the couple on the towel lean together; every 48 s she takes a photo of the moon
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, reflect, streak, text, FONT3, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // this scene's 480x270 buffer, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const toHex = rgb => '#' + rgb.map(v => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('');
const mixHex = (a, b, f) => { const A = hex(a), B = hex(b); return toHex(A.map((v, k) => lerp(v, B[k], f))); };

/* ================= Scene constants ================= */
const HZ = 152;                                       // the sea horizon, and the water line at Diamond Head's foot
const MOON = { x: 262, y: 56, r: 10 };
const VENUS = { x: 447, y: 116 };                     // the evening star, low over the sea where the sun went down
// Diamond Head from Waikiki: a shallow landward rim, a short steep summit face, then a long descending shoulder.
// Reference: Cristo Vlahos, "Diamond Head view from Waikiki.JPG" (Wikimedia Commons), from 21.27644, -157.82753.
// Photo pixels: summit (1075,805), shoulder (1150,854), lower slope (1375,931). The right descent is 300/126=2.38
// wide/tall; its upper face takes 75/300=25% of the run and 49/126=39% of the drop. Here: 148/62=2.39, 37/148=25%,
// 24/62=39%. The left foot is hidden by hotels in the photo; only its visible rim is measured, not the full width.
const DH_KEYS = [[76, 152], [128, 138], [158, 122], [180, 114], [200, 104], [212, 104], [225, 100],
  [237, 94], [250, 92], [262, 90], [267, 91], [274, 90], [287, 102], [299, 114], [311, 120], [324, 122],
  [336, 128], [348, 134], [360, 139], [373, 144], [385, 147], [397, 148], [410, 152]];
const DH = { x0: 76, x1: 410, sx: 262, top: 90 };     // feet, summit column and row
const LH = { x: 398, y: 141 };                        // the lighthouse's lantern, on the seaward point
const dhRow = new Float32Array(W).fill(HZ);           // the silhouette's top row per column
for (let k = 0; k + 1 < DH_KEYS.length; k++) {
  const [x0, y0] = DH_KEYS[k], [x1, y1] = DH_KEYS[k + 1];
  for (let x = x0; x <= x1; x++) { const u = (x - x0) / (x1 - x0); dhRow[x] = lerp(y0, y1, u * u * (3 - 2 * u)); }
}
const dhY = x => (x < DH.x0 || x > DH.x1) ? HZ : Math.round(dhRow[x] + 0.6 * Math.sin(x * 0.9) * smooth(DH.sx - 6, DH.sx - 30, x) * smooth(150, 170, x));
// the far shore: the beach under the hotels curves away from x 0 (row 165) to Diamond Head's foot (row 152)
const farY = x => x >= 216 ? HZ : Math.round(HZ + 13 * Math.pow((216 - Math.max(0, x)) / 216, 1.3));
// the near waterline in front of us, a little nearer on the right
const nearF = x => 186 + 8 * Math.pow(clamp(x, 0, W) / W, 1.4);
const nearY = x => Math.round(nearF(x));

/* ================= Sky ================= */
const SKY_P = ['#0d0e29', '#111332', '#15173a', '#1a1b43', '#20204b', '#272453', '#302859', '#3a2c5e', '#452f62',
  '#523364', '#603765', '#6f3c64', '#7e4262', '#8d4960', '#9c515d', '#ab5b5a', '#ba6657', '#c87255', '#d48055', '#de8f58'].map(hex);
const skyV = (i, y) => {
  let v = Math.pow(Math.min(1, y / HZ), 1.3) * 0.8;
  v += 0.24 * smooth(190, 470, i) * smooth(40, HZ, y);                                // the afterglow over the sea
  v += 0.05 * smooth(170, 0, i) * smooth(70, HZ, y);                                  // Waikiki's glow on the haze
  v += 0.07 * Math.max(0, 1 - Math.hypot((i - MOON.x) / 64, (y - MOON.y) / 44));      // moonlit haze
  return clamp(v, 0, 1);
};
function renderSky() {                               // down to the far beach, so the haze shows between the towers
  const h = farY(0) + 1, [c, x] = makeCanvas(W, h);
  const img = x.createImageData(W, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    const f = skyV(i, Math.min(y, HZ)) * (SKY_P.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY_P[Math.min(k, SKY_P.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const STARS = [];
{
  const r = rng(808);
  while (STARS.length < 80) {
    const x = Math.floor(r() * W), y = Math.floor(Math.pow(r(), 1.7) * 104);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 22 || skyV(x, y) > 0.42 + r() * 0.12) continue;   // dusk: none in the glow yet
    const c = r();
    STARS.push({ x, y, k: 11 + Math.floor(r() * 70), ph: r(), b: 0.2 + r() * 0.55, big: r() < 0.05,
      col: c < 0.7 ? '#e8ecff' : c < 0.86 ? '#fff0d8' : '#d0e8ff' });
  }
}
function renderMoon() {                               // a full moon just risen, warm and a little hazy
  const r = MOON.r, s = r * 2 + 1, [c, x] = makeCanvas(s, s);
  const maria = [[-3, -3, 2.6], [2, -4, 1.7], [3, 2, 2.3], [-2, 4, 1.6], [-6, 1, 1.2], [6, -1, 1.1]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -7 ? '#fff6dc' : dx + dy < 5 ? '#fbe9c4' : '#f0d8ac';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dx + dy < 0 ? '#ead4aa' : '#dcc298';
    if (dd > r - 1.1) col = dx + dy > 2 ? '#d8bc92' : '#f6e6c2';
    px(x, i, y, col);
  }
  return c;
}

/* ================= Trade-wind clouds (two depths, drifting right with the wind off the mountains) ================= */
// Each layer is a tile, periodic across its width, scrolled one tile a loop: the high wisps over 480 px (2 px/s), the
// low bank over the sea over 240 px (1 px/s, it is further off).
const CLOUD_HI = [[40, 30, 46, 5], [94, 24, 30, 4], [170, 36, 60, 5], [250, 12, 44, 4], [330, 20, 40, 4], [400, 34, 52, 5], [456, 10, 36, 3]];
const CLOUD_MID = [[20, 96, 30, 3], [60, 74, 40, 3], [150, 86, 52, 4], [236, 68, 34, 3], [290, 100, 30, 3], [330, 80, 46, 4], [420, 92, 38, 3]];
const CLOUD_LO = [[30, 124, 48, 5], [70, 130, 36, 4], [128, 118, 40, 4], [170, 127, 54, 6], [214, 134, 30, 3]];
function renderClouds(list, tw, pal, warmRows) {
  const [c, x] = makeCanvas(tw, HZ);
  const img = x.createImageData(tw, HZ), d = img.data, P = pal.map(hex);
  for (let y = 0; y < HZ; y++) for (let i = 0; i < tw; i++) {
    let v = 0;
    for (const [cx, cy, rx, ry] of list) for (const o of [-tw, 0, tw]) {
      const dx = (i - cx - o) / rx, dy = (y - cy) / ry, q = 1 - (dx * dx + dy * dy);
      if (q > 0) v = Math.max(v, q * (0.75 + 0.25 * Math.sin(i * 0.31 + cy) * Math.sin(y * 0.9 + cx)));
    }
    if (v < 0.12 + bayer(i, y) * 0.35) continue;
    const lit = clamp((y - warmRows[0]) / (warmRows[1] - warmRows[0]), 0, 1);          // lit from below by the afterglow
    const f = clamp(0.25 * v + 0.75 * lit, 0, 1) * (P.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i + 1, y + 2)) k++;
    const col = P[Math.min(k, P.length - 1)], o = (y * tw + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = Math.round(255 * (0.55 + 0.35 * Math.min(1, v * 1.6)));
  }
  x.putImageData(img, 0, 0);
  return c;
}
function drawClouds(t, cv, tw) {
  const off = scrollT(t, tw);
  for (let x = off - tw; x < W; x += tw) ctx.drawImage(cv, x, 0);
}
const scrollT = (t, tw) => Math.floor(frac(t / LOOP) * tw);

/* ================= Diamond Head ================= */
// The crater's western face, towards us: ribs of tuff running down from the rim (radiating, so the left ones lean
// left and the right ones right), the gullies between them in shadow, kiawe scrub on the lower slopes. Its rim and
// the crests of the ribs catch the afterglow (a separate layer, so it can breathe).
const RIBS = [];
{
  const r = rng(4242);
  for (let x = 160; x < 390; x += 7 + Math.floor(r() * 8)) {
    const top = dhY(x) + 1 + Math.floor(r() * 4);
    RIBS.push({ x, top, s: (x - DH.sx) / 110 + (r() - 0.5) * 0.35,
      len: Math.round((HZ - 8 - top) * (0.5 + r() * 0.5)), w: r() < 0.55 ? 2 : 1 });
  }
}
const dhInside = (i, y) => i >= DH.x0 && i <= DH.x1 && y >= dhY(i) && y <= HZ;
function renderDiamondHead() {
  const [c, x] = makeCanvas(W, HZ + 1), [g, gx] = makeCanvas(W, HZ + 1);
  for (let i = DH.x0; i <= DH.x1; i++) {
    const top = dhY(i);
    for (let y = top; y <= HZ; y++) {
      const dd = y - top, b = bayer(i, y), steep = i > DH.sx + 4;
      let col = hash(i >> 1, y >> 1, 21) < 0.25 ? '#2a1f36' : '#30243c';
      if (steep && dd > 1) col = b < 0.5 ? '#352840' : '#30243c';
      const scrub = smooth(HZ - 16, HZ - 4, y + (hash(i, 0, 22) - 0.5) * 6);           // kiawe on the lower slopes
      if (scrub > b) col = hash(i, y, 23) < 0.35 ? '#1c2526' : '#18201f';
      if (dd === 0) col = '#5e3e5a'; else if (dd === 1 && b < 0.6) col = '#46304c';
      px(x, i, y, col);
      if (dd === 0) px(gx, i, y, '#9a5a6e'); else if (dd === 1 && b < 0.45) px(gx, i, y, '#6a4058');
    }
  }
  for (const rb of RIBS) {
    for (let k = 0; k < rb.len; k++) {
      const yy = rb.top + k, xx = Math.round(rb.x + rb.s * k + 0.8 * Math.sin(k * 0.21 + rb.x));
      if (!dhInside(xx, yy) || !dhInside(xx + 1, yy)) continue;
      const fade = 1 - k / rb.len;
      if (bayer(xx, yy) > 0.35 + fade * 0.65) continue;
      px(x, xx, yy, k < 4 ? '#5a4060' : '#4a3452');
      if (rb.w > 1 && k < rb.len * 0.7) px(x, xx - 1, yy, '#3e2c48');
      px(x, xx + 1, yy, '#1c1426'); if (rb.w > 1) px(x, xx + 2, yy, '#241a2e');         // the gully beside it
      if (bayer(xx + 2, yy) < fade * 0.6) px(gx, xx, yy, fade > 0.6 ? '#7a4a62' : '#5e3c52');
    }
  }
  // the lighthouse on the point: a square white tower, the lantern on top, the keeper's house beside it
  rect(x, LH.x - 1, LH.y + 1, 3, 9, '#a49cb2'); rect(x, LH.x + 1, LH.y + 1, 1, 9, '#7a7290');
  rect(x, LH.x - 1, LH.y - 1, 3, 1, '#3a3446'); px(x, LH.x, LH.y - 2, '#3a3446');
  rect(x, LH.x - 7, LH.y + 5, 6, 5, '#8a8298'); rect(x, LH.x - 7, LH.y + 5, 6, 1, '#4a3a4e');
  rect(x, LH.x - 8, LH.y + 4, 8, 1, '#5a4656');
  return { c, glow: g };
}
// houses and lanes on the lower slopes by the point (Diamond Head Road), a few coming and going
const SLOPE = [];
{
  const r = rng(515);
  while (SLOPE.length < 26) {
    const i = 350 + Math.floor(r() * 52), y = Math.round(lerp(Math.max(dhY(i) + 5, HZ - 15), HZ - 2, r()));
    if (y >= HZ - 1 || y < dhY(i) + 4 || Math.abs(i - LH.x) < 9 && y > LH.y - 3) continue;
    SLOPE.push({ id: SLOPE.length, x: i, y, tog: r() < 0.35, n: 2 + Math.floor(r() * 6), off: r(),
      col: r() < 0.8 ? (r() < 0.5 ? '#ffd690' : '#ffbe6e') : '#cfe2ff' });
  }
}
function drawSlope(t) {
  for (const s of SLOPE) {
    if (s.tog && hash(s.id + 300, Math.floor(frac(t / LOOP + s.off) * s.n), 31) < 0.4) continue;
    px(ctx, s.x, s.y, s.col);
  }
}
const LH_K = 40;                                      // the light: one occultation every 6 s
function lighthouse(t) {                              // 0..1: the light on, with a short soft eclipse
  const u = frac(t * LH_K / LOOP);
  return u < 0.78 ? 1 : u < 0.86 ? 1 - smooth(0.78, 0.84, u) : smooth(0.88, 0.98, u);
}
function drawLighthouse(t) {
  const L = lighthouse(t);
  halo(HL.lhWide, LH.x, LH.y, 0.35 + 0.65 * L);
  halo(HL.lh, LH.x, LH.y, L);
  px(ctx, LH.x, LH.y, L > 0.5 ? '#fff6dc' : '#8a7a6a');
  px(ctx, LH.x - 5, LH.y + 7, '#ffd690'); px(ctx, LH.x - 3, LH.y + 7, '#e8b86a');      // the keeper's house, lit
}

/* ================= Waikiki: the towers along the curve of the beach ================= */
// Nearest (and tallest) on the left, falling away towards Kapahulu; past x 160 only low-rises, kept under Diamond
// Head's rim so the whole silhouette reads. Floors are 2 rows (a window row over a balcony slab), windows 2 px apart.
// T4 is the old beachfront hotel, white with a lit veranda, whose roof carries the sign for rent.
const TOWERS = [
  { x: -2, w: 33, top: 46, tone: 0, red: true }, { x: 31, w: 19, top: 77, tone: 1 }, { x: 51, w: 29, top: 60, tone: 2, red: true, sign: true },
  { x: 82, w: 56, top: 127, old: true }, { x: 140, w: 18, top: 118, tone: 1, red: true },
  { x: 160, w: 10, top: 138, tone: 2 }, { x: 171, w: 13, top: 141, tone: 0 }, { x: 185, w: 9, top: 144, tone: 1, low: true },
  { x: 195, w: 14, top: 143, tone: 2, low: true }, { x: 210, w: 8, top: 146, tone: 0, low: true },
  { x: 300, w: 15, top: 124, tone: 1, red: true }, { x: 316, w: 11, top: 132, tone: 2 },             // condos at the crater's foot
].map(b => ({ ...b, base: farY(b.x + (b.w >> 1)) - 3 }));
const T_WALL = [['#2a2a48', '#22223c', '#1a1a30'], ['#2e2a46', '#262238', '#1c1a2e'], ['#26283f', '#1f2136', '#17192b']];
const WINS = [];
{
  const r = rng(1414);
  for (const b of TOWERS) {
    if (b.old) continue;
    const step2 = b.low ? 3 : 2;
    const share = 0.3 + r() * 0.18;
    for (let y = b.top + (b.sign ? 9 : 3); y < b.base - 1; y += step2) {
      const floor = r(), dark = floor < 0.12, busy = floor > 0.85;
      for (let x = b.x + 1; x < b.x + b.w - 1; x += 2) {
      if (x < 0) continue;
      const v = r();
      WINS.push({ id: WINS.length, x, y, lit: !dark && v > (busy ? 0.15 : 1 - share), tog: v > 0.8, tv: v > 0.985, n: 4 + Math.floor(r() * 9), off: r(),
        col: r() < 0.84 ? (r() < 0.55 ? '#ffd48a' : '#ffbf70') : '#bcd8ff' });
      }
    }
  }
}
const WTOG = WINS.filter(w => w.tog && !w.tv), WTV = WINS.filter(w => w.tv);
const TV_C = ['#7aa8e8', '#9ac0f0', '#5a88d0', '#b0d0ff', '#6a98e0'];
const REDS = TOWERS.filter(b => b.red).map((b, i) => ({ x: b.x + (b.w >> 1), y: b.top - 2, ph: i * 0.27 }));
const T3 = TOWERS[2], HSIGN = { x: T3.x + 4, y: T3.top + 2 };                         // "HOTEL" along T3's crown
const OLD = TOWERS[3];
function renderCity() {
  const [c, x] = makeCanvas(W, H);
  for (const b of TOWERS) {
    const [wa, wb, wc] = b.old ? ['#6a6478', '#5a546a', '#463f56'] : T_WALL[b.tone];
    rect(x, b.x, b.top, b.w, b.base - b.top + 1, wa);
    rect(x, b.x + b.w - 3, b.top, 3, b.base - b.top + 1, wb);                          // the side away from the moon
    rect(x, b.x, b.top, b.w, 1, b.old ? '#8a8298' : '#3c3c5e');                        // the roof's edge
    if (!b.old) for (let y = b.top + 4; y < b.base; y += b.low ? 3 : 2) rect(x, b.x, y, b.w - 1, 1, wc);   // balcony slabs
    if (b.red) { rect(x, b.x + (b.w >> 1) - 1, b.top - 1, 3, 1, '#24243a'); }
  }
  for (const w of WINS) px(x, w.x, w.y, w.lit && !w.tog ? w.col : '#151628');
  // the old hotel: a white wooden front, two floors of shuttered rooms over a veranda of lit arches
  const { x: ox, w: ow, top: ot, base: ob } = OLD;
  for (let fy = ot + 3; fy < ob - 8; fy += 5) for (let wx = ox + 3; wx < ox + ow - 3; wx += 4) {
    const v = hash(wx, fy, 71);
    rect(x, wx, fy, 2, 3, v < 0.45 ? '#2a2436' : v < 0.8 ? '#d8b070' : '#a8865a');
    if (v >= 0.45) px(x, wx, fy, v < 0.8 ? '#f0cc88' : '#c09a66');
  }
  rect(x, ox, ob - 7, ow, 1, '#8a8298');
  for (let ax = ox + 2; ax < ox + ow - 3; ax += 5) {
    rect(x, ax, ob - 5, 4, 5, '#c89a5c'); rect(x, ax, ob - 5, 4, 1, '#e8bc78'); px(x, ax, ob - 5, '#6a6478'); px(x, ax + 3, ob - 5, '#6a6478');
  }
  // the far beach: a strip of sand under the hotels, the promenade's lamps, small palms along it
  for (let i = 0; i < 216; i++) { const fy = farY(i); rect(x, i, fy - 2, 1, 2, i & 1 ? '#7a6670' : '#6e5c68'); }
  for (const [pxx, ph] of [[24, 10], [46, 13], [78, 9], [142, 11], [176, 6], [204, 7]]) palmTiny(x, pxx, farY(pxx) - 2, ph);
  // Kapiʻolani Park: banyans and ironwoods at the crater's foot, a lamp here and there
  for (let i = 214; i < 302; i++) {
    const top = Math.round(HZ - 4 - 2.4 * (0.5 + 0.5 * Math.sin(i * 0.19)) * (0.6 + 0.4 * Math.sin(i * 0.057 + 1)) - (hash(i >> 2, 3, 72) < 0.3 ? 1 : 0));
    for (let y = top; y < HZ; y++) px(x, i, y, y === top ? '#24302e' : hash(i, y, 73) < 0.3 ? '#141c1c' : '#18201f');
  }
  return c;
}
function palmTiny(x, px0, base, h) {                  // a far coconut palm: a thin leaning trunk and a few fronds
  for (let k = 0; k < h; k++) px(x, px0 + Math.round(k * 0.18), base - k, '#2a2430');
  const cx = px0 + Math.round(h * 0.18), cy = base - h;
  for (const [dx, dy] of [[-3, 1], [-2, 0], [-1, -1], [0, -1], [1, -1], [2, 0], [3, 1], [-4, 2], [4, 2], [-1, 0], [1, 0], [0, 0]]) px(x, cx + dx, cy + dy, '#1c2a28');
}
function drawCity(t) {
  for (const w of WTOG) if (hash(w.id + 900, Math.floor(frac(t / LOOP + w.off) * w.n), 41) < 0.55) px(ctx, w.x, w.y, w.col);
  const tv = step(t, 480);                            // a television on in a few rooms, its light changing with the picture
  for (const w of WTV) px(ctx, w.x, w.y, TV_C[Math.floor(hash(w.id, tv, 42) * TV_C.length)]);
  drawTraffic(t);
  for (const r of REDS) {                             // aircraft warning lights on the tallest roofs, slow together
    const a = smooth(0.3, 0.5, frac(t / 2 + r.ph)) * (1 - smooth(0.7, 0.9, frac(t / 2 + r.ph)));
    px(ctx, r.x, r.y, a > 0.5 ? '#ff4a4a' : '#5a2030'); halo(HL.red, r.x, r.y, a);
  }
  const s = 0.86 + 0.14 * wave(t, 24);                // "HOTEL" on T3's crown, steady with a slow breath
  ctx.globalAlpha = s; text(ctx, 'HOTEL', HSIGN.x, HSIGN.y, '#ff9ab0'); ctx.globalAlpha = 1;
  halo(HL.hsign, HSIGN.x + 9, HSIGN.y + 2, 0.7 * s);
  for (const lx of [12, 40, 66, 96, 124, 150, 186]) { px(ctx, lx, farY(lx) - 3, '#ffe2a8'); halo(HL.prom, lx, farY(lx) - 3, 0.75); }
  for (const lx of [232, 258, 286]) { px(ctx, lx, HZ - 3, '#ffe2a8'); halo(HL.prom, lx, HZ - 3, 0.6); }   // lamps in the park
}

// Kalākaua Avenue runs behind the far beach, its traffic a row of headlights and tail lights; Diamond Head Road
// climbs round the crater's foot by the point
const CARS = [];
{ const r = rng(2020); for (let i = 0; i < 16; i++) CARS.push({ id: i, n: 5 + Math.floor(r() * 6), ph: r(), dir: i & 1 ? 1 : -1, road: i < 13 ? 0 : 1 }); }
function drawTraffic(t) {
  for (const c of CARS) {
    const { cyc, u } = cycle(t, c.n, c.ph);
    if (hash(c.id, cyc, 44) < 0.3) continue;
    let x, y;
    if (c.road === 0) { x = Math.round(c.dir > 0 ? lerp(-4, 214, u) : lerp(214, -4, u)); y = farY(x) - 3; }
    else { x = Math.round(c.dir > 0 ? lerp(318, 404, u) : lerp(404, 318, u)); y = Math.round(lerp(HZ - 3, HZ - 7, smooth(360, 404, x))); }
    if (x < 0 || x >= W) continue;
    px(ctx, x, y, '#fff2d0'); px(ctx, x - c.dir * 3, y, '#ff4a40');
    halo(HL.prom, x + c.dir, y, 0.5);
  }
}

/* ================= The sign for rent, on the old hotel's roof ================= */
// A painted panel on short legs, lit by three floodlamps on the rail at its foot, like the rooftop signs of the older
// low-rises. Its own ad: shave ice, a rainbow cone of it. BB is the face (exported as `billboard`).
const BB = { x: 88, y: 99, w: 46, h: 22 };
const BB_DECK = BB.y + BB.h + 1;                      // the lamp rail
const BB_LAMPS = [BB.x + 5, BB.x + 22, BB.x + 39];
const BB_LEGS = [BB.x + 2, BB.x + 15, BB.x + 29, BB.x + 42];
const BB_FLICK = [[47.3, 48.5], [143.1, 143.9], [211.6, 212.8]];   // the left lamp stutters now and then
function renderBillboardFrame() {
  const [c, x] = makeCanvas(W, H);
  for (const lx of BB_LEGS) { rect(x, lx, BB_DECK + 1, 1, OLD.top - BB_DECK - 1, '#34364e'); px(x, lx + 1, BB_DECK + 1, '#1a1a2c'); }
  rect(x, BB.x - 1, BB_DECK, BB.w + 2, 1, '#2a2c44');
  rect(x, BB.x - 1, BB.y - 1, BB.w + 2, BB.h + 2, '#16172a');
  rect(x, BB.x - 1, BB.y - 1, BB.w + 2, 1, '#44406a');
  rect(x, BB.x + BB.w, BB.y, 1, BB.h, '#24263f');
  rect(x, BB.x - 1, BB.y + BB.h, BB.w + 2, 1, '#3a2e3a');
  return c;
}
// the rainbow shave ice: cherry (r), lemon (y), blue (b) syrup on the snow, in a paper cup (w)
const SHAVE = ['...rrr...', '.rrrryy..', 'rrryyyyy.', 'ryyyybbbb', 'yyybbbbbb', 'kwwwwwwwk', '.wwwwwwd.', '.wwwwwwd.',
  '..wwwwd..', '..wwwwd..', '...wwd...'];
const SHAVE_C = { r: '#ff4a6a', y: '#ffd84a', b: '#3aa8ff', w: '#e8e4ec', d: '#a8a0b8', k: '#ffffff' };
const BB_GRADE = { level: 0.74, tint: '#2a1a40', mix: 0.14, wear: 0.35, seed: 11 };
const bbTone = (col, level = BB_GRADE.level, mix = BB_GRADE.mix) => { const T = hex(BB_GRADE.tint); return toHex(hex(col).map((v, k) => lerp(v * level, T[k], mix))); };
const bbInk = col => bbTone(col, 0.86, 0.08);
const BIG_V = ['101', '101', '101', '010', '010'];   // at 2x FONT3's V reads as a U: this one meets in a point
function bigText(c, s, x, y, col) {                   // FONT3 at 2x, letters 1 px apart
  let cx = x;
  for (const ch of s) {
    (ch === 'V' ? BIG_V : FONT3[ch]).forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') rect(c, cx + 2 * i, y + 2 * j, 2, 2, col); });
    cx += 7;
  }
}
function renderBillboardAd(lit) {                     // lit[k]: floodlamp k on
  const { w, h } = BB, lamps = BB_LAMPS.map(v => v - BB.x + 1);
  const c = ditherFill(w, h, ['#1a1036', '#24143e', '#301848', '#3e1e52', '#4e265a', '#603062', '#743a68'], (i, j) => {
    let v = 0.12 + 0.3 * j / (h - 1);
    lamps.forEach((l, k) => { const d = Math.hypot((i - l) / 12, (j - h) / 16); if (lit[k] && d < 1) v += 0.5 * (1 - d) ** 1.2; });
    return v;
  });
  const x = c.getContext('2d');
  gradeAd(c, BB_GRADE);
  bigText(x, 'SHAVE', 2, 2, bbTone('#120820')); bigText(x, 'SHAVE', 1, 1, bbInk('#fff0dc'));
  bigText(x, 'ICE', 9, 13, bbTone('#120820')); bigText(x, 'ICE', 8, 12, bbInk('#7ad8ff'));
  SHAVE.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (SHAVE_C[row[i]]) px(x, 36 + i, 7 + j, bbTone(SHAVE_C[row[i]])); });
  return c;
}
function bbLampLevels(t) {
  let a = 1;
  if (BB_FLICK.some(([p, q]) => t >= p && t < q)) { const hv = hash(5, Math.floor(t * 24), 9); a = hv > 0.55 ? 1 : hv > 0.3 ? 0.3 : 0; }
  return [a * (0.94 + 0.06 * wave(t, 1500)), 0.94 + 0.06 * wave(t, 1720, 0.3), 0.94 + 0.06 * wave(t, 1330, 0.6)];
}
function drawBillboard(t, pitch) {
  const L = bbLampLevels(t);
  ctx.drawImage(BBF_C, 0, 0);
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + BB.h, 0.5 + 0.15 * (L[0] + L[1] + L[2]));
  if (!PITCH.covers(t, pitch)) ctx.drawImage(L[0] > 0.5 ? BB_AD[0] : BB_AD[1], BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => { rect(ctx, lx, BB_DECK, 2, 1, L[k] > 0.5 ? '#c4b48e' : '#5a4c4a'); halo(HL.bbLamp, lx + 1, BB_DECK, L[k]); });
}

/* ================= The bay ================= */
const WATER_TOP = HZ, WATER_H = 50;
const WATER_P = ['#6e4064', '#5e3860', '#4e3258', '#402c52', '#34274a', '#2a2342', '#221f3a', '#1b1b32'].map(hex);
function renderWater() {
  const [c, x] = makeCanvas(W, WATER_H);
  const img = x.createImageData(W, WATER_H), d = img.data;
  for (let y = 0; y < WATER_H; y++) for (let i = 0; i < W; i++) {
    const Y = WATER_TOP + y;
    if (Y < farY(i)) continue;
    let v = Math.pow(Math.min(1, (Y - farY(i)) / 36), 0.7);
    v -= 0.3 * smooth(330, 470, i) * (1 - v);                                          // the afterglow on the open sea
    v -= Math.max(0, 1 - Math.abs(i - MOON.x) / 70) * 0.12 * (1 - v);
    const f = clamp(v, 0, 1) * (WATER_P.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = WATER_P[Math.min(k, WATER_P.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
// the mirror is taken in strips, each from its own stretch of shore (the beach under the hotels slopes away)
const MIRRORS = [];
for (let x0 = 0; x0 < 216; x0 += 24) MIRRORS.push({ x: x0, w: 24, top: farY(x0) });
MIRRORS.push({ x: 216, w: W - 216, top: HZ });
const WSTREAKS = [
  { x: 14, w: 3, col: '#ffc878', a: 0.24, seed: 1 }, { x: 40, w: 2, col: '#ffc070', a: 0.18, seed: 2 },
  { x: HSIGN.x + 9, w: 3, col: '#ff8aa8', a: 0.26, seed: 3 }, { x: 148, w: 2, col: '#ffcc80', a: 0.18, seed: 4 },
  { x: 310, w: 2, col: '#ffd890', a: 0.16, seed: 5 },
];
const WSTREAKS_BB = [{ x: BB.x + (BB.w >> 1), w: 4, col: '#c890c8', a: 0.2, seed: 6 }];
const RIPPLES = [];                                   // glints of the afterglow and the moon on the swell
{ const r = rng(626); for (let i = 0; i < 54; i++) RIPPLES.push({ id: i, x: Math.floor(r() * 470) + 5, y: HZ + 3 + Math.floor(Math.pow(r(), 1.3) * 30), w: 2 + Math.floor(r() * 4), n: 160 + Math.floor(r() * 120), ph: r() }); }
function drawRipples(t) {
  for (const r of RIPPLES) {
    if (r.y < farY(r.x) + 2 || r.y > nearY(r.x) - 4) continue;
    const { cyc, u } = cycle(t, r.n, r.ph);
    if (hash(r.id, cyc, 58) < 0.4) continue;
    const glow = smooth(300, 470, r.x), moon = 1 - Math.min(1, Math.abs(r.x - MOON.x) / 90);
    ctx.globalAlpha = Math.sin(Math.PI * u) * (0.14 + 0.4 * Math.max(glow, moon));
    R(r.x + Math.round((hash(r.id, cyc, 59) - 0.5) * 24), r.y, r.w, 1, glow > moon ? '#f0a07a' : '#e8dcc0');
  }
  ctx.globalAlpha = 1;
}
function drawMoonPath(t) {
  const y1 = nearY(MOON.x) - 2;
  streak(ctx, t, MOON.x, 9, '#d8a888', 0.32, 3.1, HZ + 1, y1);
  streak(ctx, t, MOON.x, 4, '#fff0cc', 0.75, 7.7, HZ + 1, y1);
  const s = step(t, 1440);
  for (let j = 0; j < 26; j++) {
    if (hash(j, s, 51) > 0.5) continue;
    const k = Math.floor(Math.pow(hash(j, s, 52), 0.8) * (y1 - HZ)), spread = 2 + k * 0.4;
    ctx.globalAlpha = 0.9 - k / 70;
    px(ctx, Math.round(MOON.x + (hash(j, s, 53) - 0.5) * 2 * spread), HZ + 1 + k, hash(j, s, 54) < 0.5 ? '#ffffff' : '#ffe8c0');
  }
  ctx.globalAlpha = 1;
}
function drawAfterglowSea(t) {                        // the last light lying along the horizon, broken by the swell
  for (let j = 0; j < 4; j++) {
    const y = HZ + 1 + j * 2, a = 0.34 - j * 0.07;
    for (let x = 396; x < W; x += 3) {
      const v = Math.sin(x * 0.37 + j * 1.9 + TAU * (30 + j * 7) * t / LOOP) + 0.6 * Math.sin(x * 0.11 - TAU * 19 * t / LOOP);
      if (v < 0.4) continue;
      ctx.globalAlpha = a * smooth(396, 440, x) * Math.min(1, v - 0.4);
      R(x, y, 2 + (v > 1.1 ? 1 : 0), 1, j < 2 ? '#ffb07a' : '#e88a78');
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= The reef break: sets, and surfers in the lineup ================= */
const SETS = 12, REEF = [{ x0: 212, x1: 286, y: 165 }, { x0: 300, x1: 372, y: 163 }];   // Canoes and Queens
function drawReef(t) {
  const { cyc, u } = cycle(t, SETS);
  REEF.forEach((b, k) => {
    const uu = u - k * 0.22;
    if (uu < 0 || uu > 0.6) return;
    const peel = smooth(0, 0.35, uu), fade = 1 - smooth(0.35, 0.6, uu), y = b.y + Math.round(uu * 6);
    const xe = Math.round(lerp(b.x1, b.x0, peel));    // it breaks at the right and peels left
    for (let x = xe; x <= b.x1; x++) {
      const hv = hash(x, cyc + k * 17, 77);
      if (hv < 0.25) continue;
      ctx.globalAlpha = fade * (0.35 + 0.5 * hv) * smooth(b.x1 + 2, b.x1 - 10, x) * smooth(b.x0 - 2, b.x0 + 8, x);
      R(x, y, 1, 1, '#e8e0ec');
      if (hv > 0.7) R(x, y - 1, 1, 1, '#b8a8c4');
    }
  });
  ctx.globalAlpha = 1;
}
// the lineup: surfers sitting on their boards, bobbing; one catches a wave at 40 s and again at 160 s
const LINEUP = [{ x: 228, y: 167, rank: 0.2 }, { x: 262, y: 166, rank: 0 }, { x: 318, y: 165, rank: 0.45 }, { x: 352, y: 166, rank: 0.65 }, { x: 290, y: 168, rank: 0.9 }];
const RIDES = [40, 160], RIDE = 8, BACK = 26;
function surferSit(x, y, skin, board) {
  R(x - 2, y, 6, 1, board); R(x, y - 3, 2, 3, '#1a1a2a'); R(x, y - 4, 2, 1, skin);
}
function drawLineup(t, crowd) {
  LINEUP.forEach((s, i) => {
    if (s.rank >= crowd) return;
    const bob = Math.round(wave(t, 48 + i * 5, i * 0.3) * 0.7);
    const ride = i === 1 ? RIDES.find(r => t >= r - 3 && t < r + RIDE + BACK) : undefined;
    if (ride === undefined) { surferSit(s.x, s.y + bob, '#8a6048', i & 1 ? '#c8c0d8' : '#d89a6a'); return; }
    const tau = t - ride;
    if (tau < 0) {                                     // paddling for it
      R(s.x - 3, s.y, 7, 1, '#c8c0d8'); R(s.x - 1, s.y - 1, 4, 1, '#1a1a2a'); px(ctx, s.x + 3, s.y - 1, '#8a6048');
      if (frac(t * 2) < 0.5) px(ctx, s.x - 2, s.y - 1, '#e8e0ec');
    } else if (tau < RIDE) {                          // up and riding left along the face, spray behind
      const u = tau / RIDE, x = Math.round(lerp(s.x, s.x - 46, u)), y = Math.round(lerp(s.y, s.y + 7, u));
      R(x - 3, y, 7, 1, '#c8c0d8'); R(x, y - 5, 2, 3, '#1a1a2a'); R(x - 1, y - 2, 1, 2, '#1a1a2a'); R(x + 2, y - 2, 1, 2, '#1a1a2a');
      R(x - 1, y - 4, 1, 1, '#8a6048'); R(x + 2, y - 4, 1, 1, '#8a6048'); R(x, y - 6, 2, 1, '#8a6048');
      ctx.fillStyle = '#ece4f0';
      for (let k = 1; k < 9; k++) if (hash(k, step(t, 960), 79) < 0.6) { ctx.globalAlpha = 0.8 - k * 0.08; ctx.fillRect(x + 3 + k, y - (k >> 1) + 1, 1, 1); }
      ctx.globalAlpha = 1;
    } else {                                          // kicked out: paddling back out to his spot
      const u = smooth(0, 1, (tau - RIDE) / BACK), x = Math.round(lerp(s.x - 46, s.x, u)), y = Math.round(lerp(s.y + 7, s.y, u));
      R(x - 3, y, 7, 1, '#c8c0d8'); R(x - 2, y - 1, 4, 1, '#1a1a2a'); px(ctx, x - 3, y - 1, '#8a6048');
      if (frac(t * 1.5) < 0.5) px(ctx, x + 2, y - 1, '#e8e0ec');
    }
  });
}

/* ================= On the water: an outrigger canoe, the sunset catamaran, a paddleboarder ================= */
const CANOE = { t0: 84, t1: 140, y: 177 };             // six paddlers, left to right
function drawOutrigger(t, crowd) {
  if (t < CANOE.t0 || t >= CANOE.t1 || crowd <= 0) return;
  const u = (t - CANOE.t0) / (CANOE.t1 - CANOE.t0), x = Math.round(lerp(-44, W + 12, u)), y = CANOE.y;
  ctx.globalAlpha = 0.45; R(x - 6, y + 1, 6, 1, '#b8a8c8'); R(x - 14, y + 2, 7, 1, '#8a7aa0'); ctx.globalAlpha = 1;   // wake
  R(x + 2, y - 4, 22, 1, '#3a2a3a');                  // the ama (float), on the far side, and its two booms
  R(x + 6, y - 3, 1, 3, '#2a1e2c'); R(x + 19, y - 3, 1, 3, '#2a1e2c');
  R(x, y - 1, 30, 2, '#5a3a34'); R(x + 1, y + 1, 28, 1, '#3a2428'); px(ctx, x + 30, y - 2, '#5a3a34'); px(ctx, x - 1, y - 1, '#5a3a34');   // the hull
  R(x + 1, y - 1, 28, 1, '#c89a5a');                  // the gunwale catching the moon
  for (let k = 0; k < 6; k++) {
    const pxx = x + 3 + k * 4, fr = (step(t, 640) + (k & 1)) & 1;   // strokes, sides changing like a crew
    R(pxx, y - 4, 2, 3, ['#2a3a5a', '#a83a3a', '#2a3a5a', '#e8c040', '#2a3a5a', '#3a8a6a'][k]); R(pxx, y - 5, 2, 1, '#7a5040');
    px(ctx, fr ? pxx + 2 : pxx - 1, y - 2, '#c8a878'); px(ctx, fr ? pxx + 3 : pxx - 2, y, '#c8a878');
  }
}
const CAT = { t0: 120, t1: 206, y: 171 };             // the sunset sail coming home across the bay, right to left
function drawCatamaran(t, crowd) {
  if (t < CAT.t0 || t >= CAT.t1) return;
  const u = (t - CAT.t0) / (CAT.t1 - CAT.t0), x = Math.round(lerp(W + 16, -40, u)), y = CAT.y;
  for (let j = 0; j < 15; j++) {                      // the sail: a tall triangle, pink in the afterglow
    const w = Math.round(j * 0.55);
    R(x + 9 - w, y - 16 + j, w + 1, 1, j < 3 ? '#f0d0d8' : j < 9 ? '#e0b0c0' : '#c890a8');
  }
  R(x + 10, y - 18, 1, 18, '#3a3048'); px(ctx, x + 10, y - 19, '#ffffff'); halo(HL.mast, x + 10, y - 19, 0.8);
  R(x, y - 1, 22, 1, '#d8d0e0'); R(x + 1, y, 20, 1, '#5a5068');   // deck and the two hulls
  if (crowd > 0) for (const k of [3, 6, 14, 17]) { R(x + k, y - 3, 1, 2, '#2a2234'); px(ctx, x + k, y - 4, '#8a6048'); }
  for (const k of [2, 8, 16]) { px(ctx, x + k, y - 2, '#ffd890'); }            // deck lights
  halo(HL.boat, x + 9, y - 2, 0.7);
  streak(ctx, t, x + 10, 3, '#ffd8b0', 0.3, 11, y + 2, y + 16);
}
function drawPaddler(t, crowd) {                      // a stand-up paddler drifting off the beach
  if (crowd <= 0.5) return;
  const x = Math.round(424 + 18 * wave(t, 1, 0.2)), y = 181, fr = step(t, 320) & 1;
  R(x - 4, y, 10, 1, '#d8d0e0'); R(x, y - 6, 2, 5, '#2a3a4a'); R(x, y - 8, 2, 2, '#8a6048'); R(x, y - 1, 1, 1, '#1a1a2a');
  for (let k = 0; k < 8; k++) px(ctx, x + (fr ? 2 + (k >> 2) : -1 - (k >> 2)), y - 7 + k, '#9a8a7a');
  ctx.globalAlpha = 0.3; R(x - 4, y + 1, 10, 1, '#e8dcc0'); ctx.globalAlpha = 1;
}

/* ================= Sky visitors: a night flight, a shooting star ================= */
function drawPlane(t) {
  if (t < 96 || t >= 150) return;
  // Both ends clear the frame by more than the landing-light halo's radius.
  const u = (t - 96) / 54, x = lerp(492, 334, u), y = lerp(128, -12, Math.pow(u, 0.85));
  rect(ctx, Math.round(x) - 1, Math.round(y), 4, 1, '#2a2a3a');
  halo(HL.landing, x - 1, y, 0.7);
  px(ctx, Math.round(x) - 1, Math.round(y), '#ffffff');
  if (frac(t) < 0.12) { px(ctx, Math.round(x) + 2, Math.round(y) - 1, '#ff4040'); halo(HL.red, x + 2, y - 1, 0.6); }
}
const SHOOTS = [{ t: 62.5, x: 150, y: 14, dx: -60, dy: 22 }, { t: 188.3, x: 214, y: 10, dx: 64, dy: 24 }];
function drawShootingStars(t) {
  for (const s of SHOOTS) {
    const u = (t - s.t) / 1.1;
    if (u < 0 || u > 1) continue;
    const a = Math.sin(Math.PI * u);
    for (let k = 0; k < 16; k++) {
      const uu = u - k * 0.02;
      if (uu < 0) break;
      ctx.globalAlpha = a * (1 - k / 16);
      px(ctx, Math.round(s.x + s.dx * uu), Math.round(s.y + s.dy * uu), k < 2 ? '#ffffff' : '#d8e8ff');
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= The beach in front of us ================= */
const TORCHES = [{ x: 154, base: 236 }, { x: 300, base: 240 }, { x: 446, base: 238 }].map((o, i) => ({ ...o, i, fy: 203 - (i & 1) }));
const SAND = ['#241e32', '#2c2438', '#362c3e', '#423444', '#4e3c48', '#5c464e', '#6c5052', '#7e5c56', '#906858', '#a2765c', '#b4865f'].map(hex);
function sandLight(x, y) {
  let v = 0;
  for (const o of TORCHES) { const d = Math.hypot((x - o.x) / 54, (y - o.fy - 6) / 26); if (d < 1) v = Math.max(v, (1 - d) ** 1.3); }
  return v;
}
function renderSand() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let i = 0; i < W; i++) for (let y = Math.max(0, nearY(i) - 1); y < H; y++) {
    const dy = y - nearF(i);
    let v = dy < 6 ? 0.14 + dy * 0.02 : 0.36 + 0.07 * Math.sin(i * 0.05 + y * 0.11) * Math.sin(i * 0.013) + 0.05 * Math.sin(i * 0.31 + y * 0.9) * smooth(200, 240, y);   // wet, then dry sand
    v += 0.05 * smooth(160, 300, MOON.x - Math.abs(i - MOON.x) * 2);                     // moonlight
    v += 0.55 * sandLight(i, y);
    v -= 0.12 * smooth(225, 270, y);
    if (dy >= 6 && hash(i, y, 91) < 0.06) v += 0.08;                                      // footprints and ripples in the sand
    if (dy >= 6 && hash(i >> 1, y, 92) < 0.03) v -= 0.1;
    const f = clamp(v, 0, 1) * (SAND.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SAND[Math.min(k, SAND.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  for (const o of TORCHES) {                          // bamboo torch poles (the flames are drawn per frame)
    for (let y = o.fy + 4; y < o.base; y++) { px(x, o.x, y, '#5a4630'); px(x, o.x + 1, y, y % 5 === 0 ? '#2a1e18' : '#3a2c20'); }
    rect(x, o.x - 1, o.fy + 1, 4, 3, '#3a3036'); rect(x, o.x - 1, o.fy + 1, 4, 1, '#6a5a50');
    rect(x, o.x - 2, o.base - 1, 6, 1, '#1a1622');
  }
  for (let k = 0; k < 15; k++) {                      // footprints up from the water, someone gone for a swim
    const u = k / 14, fx = Math.round(lerp(262, 236, u) + (k & 1 ? 2 : -2)), fy = Math.round(lerp(nearY(262) + 7, 262, u * u));
    rect(x, fx, fy, 2, 1, '#2a2234'); px(x, fx, fy + 1, '#3a3040');
  }
  return c;
}
// beach naupaka in the corners, in front of us: three frames of the leaves stirring in the trade wind
const BUSHES = [[-4, 240, 26, 16, 11], [28, 254, 34, 12, 12], [78, 263, 26, 9, 13], [424, 258, 24, 10, 14], [486, 244, 30, 18, 15]];
function renderBushes(f) {
  const [c, x] = makeCanvas(W, H);
  for (const [cx, cy, rx, ry, seed] of BUSHES) {
    for (let y = cy - ry - 1; y < H; y++) for (let i = cx - rx - 1; i <= cx + rx + 1; i++) {
      if (i < 0 || i >= W) continue;
      const sw = (f - 1) * Math.max(0, (cy - y) / ry);                               // the tops move most
      const dx = (i - cx - sw) / rx, dy = (y - cy) / ry, q = 1 - dx * dx - (dy < 0 ? dy * dy : 0);
      if (q < 0.12 * hash(i, y, seed)) continue;
      const hx = Math.round(i - sw * 1.5);
      const lit = dy < -0.35 + 0.35 * hash(hx >> 1, y >> 1, seed + 1) && hash(hx >> 1, y, seed + 5 + f) < 0.75;
      let col = lit ? (hash(hx, y, seed + 2) < 0.5 ? '#2c4638' : '#385644') : hash(i, y, seed + 3) < 0.4 ? '#1a2824' : '#141e1c';
      if (lit && hash(hx, y, seed + 4) < 0.045) col = '#c8c0d0';
      px(x, i, y, col);
    }
  }
  return c;
}
// shore break: small waves every 8 s, a set of three bigger ones then three small
const WAVE_P = 8, WAVE_LIFE = 16, SET_AMP = [1, 0.8, 0.65, 0.4, 0.35, 0.45];
function waveDist(u, big) {                           // front distance above the waterline (px); < 0 = up the sand
  const run = 4 + 3 * big;
  if (u < 0.7) return 13 - 11 * Math.pow(u / 0.7, 1.2);
  if (u < 0.84) return 2 - run * smooth(0.7, 0.84, u);
  return 2 - run + 0.5 * run * smooth(0.84, 1, u);
}
function drawWaves(t) {
  const n0 = Math.floor(t / WAVE_P), sp = step(t, 1440);
  for (let j = 2; j >= 0; j--) {
    const n = n0 - j, u = (t - n * WAVE_P) / WAVE_LIFE;
    if (u >= 1) continue;
    const id = ((n % 30) + 30) % 30, big = SET_AMP[id % 6] * (0.85 + 0.15 * hash(id, 81));
    for (let x = 0; x < W; x++) {
      const ux = u + 0.03 * Math.sin(x * 0.012 + id * 1.3) + 0.02 * Math.sin(x * 0.041 + id);
      if (ux < 0 || ux >= 1) continue;
      const gap = Math.sin(x * 0.07 + id * 1.7) + 0.6 * Math.sin(x * 0.19 + id * 0.7);
      if (gap < -0.7 + (1 - big) * 1.1 - (ux > 0.62 ? 0.6 : 0) + 0.4 * Math.sin(x * 0.023 + id * 2.1)) continue;
      const sy = nearF(x), y = Math.round(sy - waveDist(ux, big));
      if (ux < 0.45) {                                 // swell: a soft lit line over its shadow
        ctx.globalAlpha = smooth(0, 0.3, ux) * (0.25 + 0.35 * big); R(x, y, 1, 1, '#8a6a8a');
        ctx.globalAlpha *= 0.8; R(x, y + 1, 1, 1, '#141428');
      } else if (ux < 0.7) {                          // breaking: foam, a little spray
        const lace = hash(x >> 1, id, 86);
        ctx.globalAlpha = 0.45 + 0.45 * lace; R(x, y, 1, 1, lace < 0.2 ? '#a89ab8' : '#ece6f0');
        if (lace > 0.75) { ctx.globalAlpha = 0.5; R(x, y - 1, 1, 1, '#d8cce0'); }
        if (big > 0.6) { ctx.globalAlpha = 0.6; R(x, y + 1, 1, 1, '#b8aac8'); }
        const hv = hash(x, id, sp);
        if (hv < 0.35 * big) { ctx.globalAlpha = 0.5 + hv; R(x, y - 1 - Math.floor(hv * 4), 1, 1, '#d8cce0'); }
      } else {                                         // the run-up sheet on the sand, its lace edge, backwash
        const f = 1 - smooth(0.84, 1, ux), top = Math.min(y, Math.round(sy)), hh = Math.abs(y - Math.round(sy)) + 1;
        ctx.globalAlpha = 0.35 * f; R(x, top, 1, hh, '#8a80a8');
        ctx.globalAlpha = 0.85 * f; R(x, y, 1, 1, hash(x, id, 82) < 0.2 ? '#6a6090' : '#f0ecf4');
      }
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Things on the sand (each a sprite, drawn in feet order with the people) ================= */
function sprite(w, h, draw) { const [c, x] = makeCanvas(w, h); draw(x); return c; }
// the board rack: two posts and three rails, a row of longboards standing in it (taller than the people), a few slots empty
const RACK = { x: 6, w: 60, fy: 206 };
const RACK_H = 36;
const BOARD_C = ['#d86a5a', '#e8c86a', '#6aa8d8', '#e8e0d0', '#5ab89a', '#e88aa8', '#a87ad8', '#e8a050', '#7ac8e0', '#d8d8e8'];
function renderRack() {
  return sprite(RACK.w, RACK_H, x => {
    const r = rng(77), B = RACK_H - 1;
    for (const ry of [B - 22]) rect(x, 0, ry, RACK.w, 1, '#4a4a5e');                    // the back rail, behind the boards
    for (let bx = 3; bx < RACK.w - 5; bx += 4) {
      if (r() < 0.18) continue;
      const h = 26 + Math.floor(r() * 8), col = mixHex(BOARD_C[Math.floor(r() * BOARD_C.length)], '#1c1630', 0.42);
      const lit = mixHex(col, '#ffd8a8', 0.22), dark = mixHex(col, '#000000', 0.3), top = B - h;
      rect(x, bx, top + 2, 3, h - 2, col); rect(x, bx, top + 2, 1, h - 2, lit); rect(x, bx + 2, top + 2, 1, h - 2, dark);
      px(x, bx + 1, top, col); rect(x, bx, top + 1, 3, 1, col); px(x, bx, top + 1, lit);   // the rounded nose
      if (r() < 0.5) rect(x, bx, top + 6 + Math.floor(r() * 6), 3, 1, mixHex('#e8e0d0', '#1c1630', 0.4));   // a stripe
    }
    for (const ry of [B - 14, B - 4]) rect(x, 0, ry, RACK.w, 1, '#62627a');               // the front rails, padlocked
    for (let k = 8; k < RACK.w - 4; k += 13) rect(x, k, B - 13, 1, 2, '#9a8a5a');
    rect(x, 0, B - 24, 2, 25, '#6a6a80'); rect(x, RACK.w - 2, B - 24, 2, 25, '#4a4a5e');
  });
}
// the lifeguard tower: a little pastel hut on stilts with a hipped roof, shutters down for the night, a railed deck,
// its number over the door, a ramp, and a red rescue board leaning on a leg
const TOWER = { x: 92, fy: 203 }, TOWER_H = 32;
function renderTower() {
  return sprite(36, TOWER_H, x => {
    const B = TOWER_H - 1;
    for (let k = 0; k < 5; k++) rect(x, 12 - k * 2 + 4, k, 6 + k * 4, 1, k ? '#6a5a6e' : '#8a7a8a');   // the roof
    rect(x, 7, 5, 24, 1, '#3e3448');
    rect(x, 9, 6, 20, 10, '#6aa0a8'); rect(x, 26, 6, 3, 10, '#4a7a84');               // the walls, the shaded side
    rect(x, 11, 8, 8, 6, '#4a7a84');                                                   // the shutter, down
    for (let sy = 9; sy < 14; sy += 2) rect(x, 11, sy, 8, 1, '#3a6470');
    text(x, '2F', 20, 9, '#f0e4c0');
    rect(x, 7, 16, 24, 1, '#3a4a56'); for (let k = 7; k < 31; k += 3) rect(x, k, 13, 1, 3, '#8a9aa8');   // the deck's railing
    rect(x, 7, 13, 24, 1, '#8a9aa8');
    for (const lx of [9, 28]) rect(x, lx, 17, 1, B - 17, '#7a7a8e');
    for (const lx of [14, 23]) rect(x, lx, 17, 1, B - 17, '#55556a');
    rect(x, 9, 22, 20, 1, '#55556a');                                                  // a brace
    for (let k = 0; k < 13; k++) rect(x, 7 - Math.round(k * 0.55), 17 + k, 2, 1, '#8a7a62');   // the ramp
    for (let k = 0; k < 14; k++) rect(x, 31 + (k > 10 ? 1 : 0), B - 13 + k, 2, 1, k < 2 ? '#e86a4a' : '#c84a3a');   // the rescue board
  });
}
// an outrigger canoe drawn up on the sand: a long dark hull with its bow and stern swept up, the ama (float) on the far
// side on two curved booms, a paddle across the seats
const BEACHED = { x: 186, fy: 216 };
function renderBeached() {
  return sprite(72, 14, x => {
    rect(x, 10, 2, 50, 2, '#3a3040'); px(x, 9, 3, '#3a3040'); px(x, 60, 3, '#3a3040'); rect(x, 10, 2, 50, 1, '#5a4a5a');   // the ama
    for (const bx of [20, 48]) { px(x, bx, 3, '#6a5a50'); px(x, bx, 4, '#6a5a50'); px(x, bx + 1, 5, '#6a5a50'); px(x, bx + 1, 6, '#6a5a50'); px(x, bx + 1, 7, '#6a5a50'); }
    rect(x, 4, 8, 62, 3, '#6a2a2e'); rect(x, 6, 11, 58, 1, '#3a1a20');                // the hull
    rect(x, 1, 6, 3, 2, '#6a2a2e'); px(x, 0, 5, '#6a2a2e'); px(x, 3, 8, '#6a2a2e');   // the bow, swept up
    rect(x, 66, 6, 3, 2, '#6a2a2e'); px(x, 69, 5, '#6a2a2e'); px(x, 66, 8, '#6a2a2e');
    rect(x, 2, 7, 66, 1, '#d8b060');                  // the gunwale, catching the torchlight
    for (const sx of [14, 26, 38, 50]) rect(x, sx, 8, 3, 1, '#4a1e22');                 // the seats
    rect(x, 2, 12, 66, 1, '#1a1622');                 // its shadow on the sand
    for (let k = 0; k < 18; k++) px(x, 24 + k * 2, 6 - (k >> 3), '#8a6a48');            // a paddle across it
    rect(x, 58, 4, 3, 2, '#8a6a48');
  });
}
// the couple's towel, their slippers and a cooler (static); the man with the ukulele sits on another cooler
const TOWEL = { x: 330, y: 205 }, UKE = { x: 418, fy: 206 };
function renderTowel() {
  return sprite(34, 6, x => {
    for (let i = 0; i < 26; i++) rect(x, i + 2, 1, 1, 3, i % 6 < 3 ? '#a85a5a' : '#c8b490');
    rect(x, 2, 4, 26, 1, '#5a3a40');
    rect(x, 29, 2, 3, 1, '#e8c86a'); rect(x, 29, 4, 3, 1, '#e8c86a');                  // her slippers
    rect(x, 0, 1, 1, 3, '#3a2a34');
  });
}
function renderCooler() {
  return sprite(10, 7, x => {
    rect(x, 0, 1, 10, 6, '#4a8ab8'); rect(x, 0, 0, 10, 2, '#e8e4ec'); rect(x, 3, 0, 4, 1, '#8a8a9a'); rect(x, 0, 6, 10, 1, '#1a1622');
  });
}

/* ================= People ================= */
// One function draws everyone near us (after rome.js): a head, a body, legs, a colour each, mixed towards the night and
// lit along the shoulders by the torches. Feet on row fy, centred on column x; returns the top of the head.
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const NIGHT = '#140e26', GLOWC = '#ffcc98';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.28, m = c => mixHex(c, NIGHT, dk), tc = top || SKINS[skin];
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(tc), topD: mixHex(tc, NIGHT, dk + 0.3),
    topL: mixHex(tc, GLOWC, 0.35), bot: m(bot), shoe: m('#2a2026'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    print: o.print ? m(o.print) : null, hat: o.hat ? m(o.hat) : null };
}
// o.view: 0 from behind, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated with the seat on
// row fy; o.lean: head px towards a neighbour; o.aR: 0 down, 2 bent up holding a phone, 3 out
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
      R(x - 1, ly, 2, 2, P.skirt || P.bot);
    } else {
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, 2, legs - 2, legC); R(tx + bw - 2, ly + 2, 2, legs - 2, legC);
      R(tx, fy, 2, 1, P.shoe); R(tx + bw - 2, fy, 2, 1, P.shoe);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (P.print) for (let j = 1; j < th; j += 2) for (let i = (j >> 1) & 1; i < bw; i += 2) R(tx + i, ty + j, 1, 1, P.print);   // an aloha shirt
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                           // the torchlight on the shoulders
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 2) {
        R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
        const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#7ab8e8'); }
      }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}
// People walking the waterline, one at a time, each crossing once a loop (feet on the wet sand, lane rows below it)
const WALK = [
  { t0: 6, t1: 66, dir: -1, lane: 7, rank: 0.1, gap: 6,
    P: [look(19, 1, 2, '#e87a9a', '#e87a9a', { long: 3, skirt: '#e87a9a' }), look(21, 2, 0, '#3a6ab8', '#b09a70', { print: '#e8e0c8' })] },
  { t0: 74, t1: 118, dir: 1, lane: 5, rank: 0.5, board: '#e8c86a', P: [look(21, 2, 1, null, '#2a6a8a')] },
  { t0: 128, t1: 182, dir: -1, lane: 9, rank: 0.3, dog: true, P: [look(19, 0, 3, '#5ab89a', '#33466e', { long: 2, hat: '#d8c8a0' })] },
  { t0: 200, t1: 234, dir: 1, lane: 6, rank: 0.6, jog: true, P: [look(20, 3, 0, '#e8c040', '#22202c')] },
];
const walkFy = (x, lane) => Math.min(206, nearY(clamp(x, 0, W - 1)) + lane);
function walkers(t, crowd, put) {
  for (const w of WALK) {
    if (w.rank >= crowd || t < w.t0 || t >= w.t1) continue;
    const u = (t - w.t0) / (w.t1 - w.t0), x = Math.round(w.dir > 0 ? lerp(-18, W + 18, u) : lerp(W + 18, -18, u));
    const fr = Math.floor(t * (w.jog ? 6 : 3.2)) & 1;
    w.P.forEach((P, k) => {
      const xx = x - k * w.dir * (w.gap || 0), fy = walkFy(xx, w.lane) + (k ? 1 : 0);
      put(fy, () => {
        person(xx, fy - (w.jog && fr ? 1 : 0), P, { view: 2, dir: w.dir, step: fr ^ k, aR: w.board ? 3 : 0 });
        if (k) R(Math.min(xx, xx + w.dir * w.gap) + 2, fy - P.ll - 2, w.gap - 3, 1, P.skin);   // hand in hand
        if (w.board) {                                 // the longboard under his arm, nose first
          const by = fy - P.ll - 1, bx = w.dir > 0 ? xx - 7 : xx - 16;   // at his hip, the nose out in front
          R(bx, by, 24, 2, mixHex(w.board, NIGHT, 0.3)); R(bx, by, 24, 1, mixHex(w.board, GLOWC, 0.2));
          px(ctx, w.dir > 0 ? bx + 24 : bx - 1, by + 1, mixHex(w.board, NIGHT, 0.3));
        }
      });
      if (w.dog && !k) {
        const dx = xx + w.dir * 9, dfy = walkFy(dx, w.lane) - 1;
        put(dfy, () => {
          R(dx - 2, dfy - 3, 5, 2, '#a8784a'); R(w.dir > 0 ? dx + 2 : dx - 3, dfy - 5, 2, 3, '#a8784a'); px(ctx, w.dir > 0 ? dx + 3 : dx - 3, dfy - 5, '#3a2418');
          px(ctx, w.dir > 0 ? dx - 3 : dx + 3, dfy - 4, '#a8784a');                       // the tail, up
          px(ctx, dx - 2 + fr, dfy - 1, '#7a5638'); px(ctx, dx + 2 - fr, dfy - 1, '#7a5638'); px(ctx, dx - 1, dfy, '#7a5638'); px(ctx, dx + 1, dfy, '#7a5638');
          ctx.globalAlpha = 0.7; for (let k2 = 1; k2 < 8; k2++) px(ctx, Math.round(lerp(xx + w.dir * 2, dx, k2 / 8)), Math.round(lerp(dfy - 13, dfy - 4, k2 / 8) + 1.2 * Math.sin(Math.PI * k2 / 8)), '#3a2a2a');
          ctx.globalAlpha = 1;
        });
      }
    });
  }
}
// the couple on the towel, watching the moon come up: they lean together every 30 s, she takes a photo every 48 s
const COUPLE = [look(24, 0, 2, '#e8806a', '#5a7ab0', { long: 4 }), look(26, 2, 0, '#3a8a7a', '#33466e', { print: '#d8e8c0' })];
function couple(t, put) {
  const lean = frac(t / 30), photo = frac(t / 48), fy = TOWEL.y;
  put(fy + 0.6, () => {
    const l = lean > 0.25 && lean < 0.72 ? 1 : 0;
    person(TOWEL.x + 11, fy, COUPLE[0], { sit: true, lean: l, aR: photo > 0.3 && photo < 0.52 ? 2 : 0 });
    person(TOWEL.x + 20, fy, COUPLE[1], { sit: true, lean: -l });
  });
}
// the man with the ukulele, on a cooler under the palm, facing the sea: strumming, nodding with it
const UKER = look(22, 3, 0, '#d8584a', '#2a3450', { print: '#f0d8a0' });
function ukulele(t, put) {
  const seat = UKE.fy - 6;
  put(UKE.fy, () => {
    ctx.drawImage(COOLER_C, UKE.x - 4, seat);
    const P = UKER, x = UKE.x;
    R(x - 5, seat - 1, 5, 2, P.bot); R(x - 6, seat + 1, 2, 5, P.skin); R(x - 7, UKE.fy, 3, 1, P.shoe);   // thighs, shins, slippers
    person(x, seat, P, { sit: true, view: 2, dir: -1, lean: frac(t / 2) < 0.5 ? 0 : -1 });   // nodding with the tune
    const ty = seat - P.th + 1, strum = step(t, 1440) & 1;
    R(x - 5, ty + 3, 4, 3, '#a8703a'); R(x - 5, ty + 4, 1, 1, '#3a2418');                // the ukulele's body, its sound hole
    for (let k = 1; k < 6; k++) px(ctx, x - 5 - k, ty + 3 - (k >> 1), '#6a4426');      // its neck, up towards the sea
    px(ctx, x - 9, ty + 1, P.skin);                                                   // the hand on the frets
    R(x - 2, ty + 3 + strum, 2, 1, P.skin); R(x - 1, ty + 1, 1, 2 + strum, P.topD);  // the strumming hand
  });
}

/* ================= Torches, the palm, birds and moths ================= */
function makePool(rx, ry, col, maxA) {             // an elliptical dithered pool of light, drawn additively
  const rgb = hex(col), [c, x] = makeCanvas(rx * 2 + 1, ry * 2 + 1);
  const img = x.createImageData(rx * 2 + 1, ry * 2 + 1), d = img.data;
  for (let y = 0; y <= ry * 2; y++) for (let i = 0; i <= rx * 2; i++) {
    const dd = Math.hypot((i - rx) / rx, (y - ry) / ry);
    if (dd >= 1) continue;
    const q = Math.floor(Math.pow(1 - dd, 1.5) * 6 + bayer(i, y)) / 6;
    if (q <= 0) continue;
    const o = (y * (rx * 2 + 1) + i) * 4; d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(Math.min(1, q) * maxA * 255);
  }
  x.putImageData(img, 0, 0);
  return { c, r: rx, ry };
}
function drawPools(t) {                               // the torchlight on the sand, flickering with the flames
  for (const o of TORCHES) {
    const a = 0.62 + 0.2 * wave(t, 731 + o.i * 37, o.i * 0.3) + 0.18 * hash(o.i, step(t, 2400), 3);
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = a;
    ctx.drawImage(HL.pool.c, o.x + 1 - HL.pool.r, o.fy + 16 - HL.pool.ry);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
// ghost crabs (ʻōhiki): pale, quick, out on the sand after dark; each dashes, stops, dashes, and now and then ducks
// into its burrow
const CRABS = [{ x: 120, y: 226, rx: 30 }, { x: 262, y: 236, rx: 36 }, { x: 372, y: 222, rx: 26 }, { x: 200, y: 250, rx: 34 }]
  .map((c, i) => ({ ...c, i }));
function drawCrabs(t) {
  for (const c of CRABS) {
    const n = 40 + c.i * 4, { cyc, u } = cycle(t, n, c.i * 0.17);       // a move every 5-6 s
    const at = k => [c.x + Math.round((hash(c.i, k, 45) - 0.5) * 2 * c.rx), c.y + Math.round((hash(c.i, k, 46) - 0.5) * 10)];
    const [x0, y0] = at((cyc + n - 1) % n), [x1, y1] = at(cyc), m = smooth(0, 0.18, u);
    if (hash(c.i, cyc, 47) < 0.2 && u > 0.3) continue;                   // gone down its hole
    const x = Math.round(lerp(x0, x1, m)), y = Math.round(lerp(y0, y1, m)), run = m > 0 && m < 1;
    R(x, y - 1, 3, 1, '#c8b8a8'); px(ctx, x, y - 2, '#e8dcc8'); px(ctx, x + 2, y - 2, '#e8dcc8');
    px(ctx, x - (run && (step(t, 1920) & 1) ? 1 : 0), y, '#9a8a7a'); px(ctx, x + 3, y, '#9a8a7a');
    px(ctx, x + 1, y, '#3a3040');
  }
}
function drawTorches(t) {
  for (const o of TORCHES) {
    rect(ctx, o.x - 1, o.fy + 1, 4, 3, '#3a3036'); rect(ctx, o.x - 1, o.fy + 1, 4, 1, '#6a5a50');   // the canister
    const s = step(t, 2400), f = 0.85 + 0.15 * hash(o.i, s, 3), hgt = 4 + Math.round(hash(o.i, s, 4) * 2.4);
    halo(HL.torch, o.x + 1, o.fy - 2, f); halo(HL.torchCore, o.x + 1, o.fy - 1, f);
    const lean = Math.round(wave(t, 60 + o.i * 7, o.i * 0.2) * 0.8);
    R(o.x - 1, o.fy - 1, 4, 2, '#e85a20');
    for (let k = 0; k < hgt; k++) {
      const w = k < 1 ? 3 : k < hgt - 2 ? 2 : 1, xx = o.x + (k > 1 ? lean : 0) + (w === 3 ? -1 : 0) + (w === 1 && hash(o.i, s, 5) < 0.5 ? 1 : 0);
      R(xx, o.fy - 1 - k, w, 1, k < 2 ? '#fff0a0' : k < hgt - 1 ? '#ffb040' : '#ff7a30');
    }
    for (let e = 0; e < 3; e++) {                    // an ember lifting off now and then
      const { cyc, u } = cycle(t, 50 + e * 11, o.i * 0.37 + e * 0.29);
      if (hash(o.i, cyc, 60 + e) < 0.55 || u > 0.7) continue;
      ctx.globalAlpha = 1 - u / 0.7;
      px(ctx, Math.round(o.x + 1 + (hash(o.i, cyc, 70 + e) - 0.5) * 6 + u * 4), Math.round(o.fy - 6 - u * 20), u < 0.3 ? '#ffd070' : '#ff8a40');
    }
    ctx.globalAlpha = 1;
  }
}
// the leaning coconut palm in front, on the right: a trunk curving up from the sand, its crown over the sea
const PALM = { p0: [470, 272], p1: [484, 160], p2: [420, 62] };
const bez = (s, k) => (1 - s) * (1 - s) * PALM.p0[k] + 2 * (1 - s) * s * PALM.p1[k] + s * s * PALM.p2[k];
function renderTrunk() {
  const [c, x] = makeCanvas(W, H);
  for (let s = 0; s <= 1; s += 0.002) {
    const cx = bez(s, 0), cy = Math.round(bez(s, 1)), w = lerp(8, 4, s), x0 = Math.round(cx - w / 2), wi = Math.round(w);
    if (cy >= H) continue;
    const ring = (Math.round(cy) % 3) === 0, torch = smooth(190, 230, cy);
    for (let i = 0; i < wi; i++) {
      const e = i / Math.max(1, wi - 1);
      let col = ring ? '#241c26' : e < 0.25 ? mixHex('#5a4a5a', '#9a6a4a', torch) : e > 0.75 ? '#1e1820' : '#342a34';
      if (!ring && e < 0.25 && hash(i, cy, 99) < 0.2) col = '#463a46';
      px(x, x0 + i, cy, col);
    }
  }
  return c;
}
const PALM_NF = 7, CROWN = { w: 120, h: 78, ox: 62, oy: 30 };
const FRONDS = [[-2.85, 30, 0.5], [-2.4, 32, 0.35], [-1.95, 19, 0.3], [-1.4, 17, 0.25], [-0.95, 27, 0.25], [-0.5, 31, 0.45],
  [-0.05, 32, 0.6], [0.45, 28, 0.75], [2.7, 30, 0.75], [3.15, 32, 0.6], [1.2, 22, 0.6], [1.9, 24, 0.7]];
function renderCrown(sw) {
  const [c, x] = makeCanvas(CROWN.w, CROWN.h);
  const P = (gx, gy, col) => { gx = Math.round(gx); gy = Math.round(gy); if (gx >= 0 && gy >= 0 && gx < CROWN.w && gy < CROWN.h) px(x, gx, gy, col); };
  FRONDS.forEach(([a0, L, droop], fi) => {
    const a = a0 + sw * 0.05 * (1 + droop);
    for (let k = 0; k <= L; k++) {
      const s = k / L, gx = CROWN.ox + Math.cos(a) * k, gy = CROWN.oy + Math.sin(a) * k + droop * L * s * s * 0.9 + sw * s * s * 2;
      P(gx, gy, s < 0.15 ? '#2a2a24' : '#24362e');
      const leaf = Math.round((1 - s) * 7 + 2) * (s < 0.08 ? 0 : 1);
      if (k % 2) continue;
      for (const sg of [-1, 1]) for (let j = 1; j <= leaf; j++) {   // leaflets hanging off both sides of the rib
        const lx = gx + sg * Math.sin(a) * j * 0.6, ly = gy - sg * Math.cos(a) * j * 0.6 + j * 0.55;
        P(lx, ly, j === leaf ? '#18241f' : (sg < 0) === (Math.sin(a) > 0) ? '#3a5a4a' : '#1c2c26');
      }
    }
    if (fi === 6 || fi === 1) for (let k = 4; k < L; k += 3) P(CROWN.ox + Math.cos(a) * k, CROWN.oy + Math.sin(a) * k + droop * L * (k / L) ** 2 * 0.9 - 1, '#56786a');   // moonlit rib
  });
  for (const [dx, dy] of [[-2, 2], [1, 3], [-1, 5], [2, 1]]) { P(CROWN.ox + dx, CROWN.oy + dy, '#5a4428'); P(CROWN.ox + dx + 1, CROWN.oy + dy, '#3a2c1a'); P(CROWN.ox + dx, CROWN.oy + dy + 1, '#3a2c1a'); }
  return c;
}
function drawPalm(t) {
  ctx.drawImage(TRUNK_C, 0, 0);
  const a = 0.7 * wave(t, 14) + 0.3 * wave(t, 37, 0.3), f = Math.round((a + 1) / 2 * (PALM_NF - 1));
  ctx.drawImage(CROWN_C[f], PALM.p2[0] - CROWN.ox, PALM.p2[1] - CROWN.oy);
}
// a pair of white fairy terns (manu-o-Kū, Honolulu's bird) gliding past the palm twice a loop
const TERNS = [{ t0: 30, t1: 64, y: 84 }, { t0: 150, t1: 184, y: 76 }];
function drawTerns(t) {
  for (const b of TERNS) {
    if (t < b.t0 || t >= b.t1) continue;
    const u = (t - b.t0) / (b.t1 - b.t0);
    for (let k = 0; k < 2; k++) {
      const x = Math.round(lerp(W + 8, -14, u) + k * 9), y = Math.round(b.y + k * 3 + 4 * Math.sin(u * 5 + k));
      const fl = (step(t, 960) + k) & 1;
      px(ctx, x, y, '#e8e4f0'); px(ctx, x + 1, y, '#e8e4f0');
      px(ctx, x - 1, y - (fl ? 1 : -1), '#d0c8dc'); px(ctx, x + 2, y - (fl ? 1 : -1), '#d0c8dc');
    }
  }
}
const MOTHS = [{ k: 70, ph: 0.1, rx: 6, ry: 5 }, { k: 83, ph: 0.55, rx: 8, ry: 6 }];
function drawMoths(t) {
  const o = TORCHES[2];
  for (const m of MOTHS) px(ctx, Math.round(o.x + 1 + m.rx * wave(t, m.k, m.ph)), Math.round(o.fy - 6 + m.ry * wave(t, m.k * 2, m.ph + 0.2)), '#f0e0c0');
}

/* ================= The picked weather ================= */
// Honolulu draws no falling weather of its own (its clouds, the surf and the torches stay in every pick): far drops end
// on the bay, mid ones on the sand where we are, near ones fall past us and out of the bottom, catching the torches.
const WEATHER = weatherPlans([]);
const WX_LAND = [[HZ - 2, HZ + 16], [190, 206], [H + 4, H + 4]];
const WX_LIGHTS = [...TORCHES.map(o => ({ x: o.x + 1, y: o.fy - 3, rx: 22, ry: 26, col: '#ffb060' })), { x: LH.x, y: LH.y, rx: 6, ry: 6, col: '#fff0c8' }];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + (BB.h >> 1), rx: 24, ry: 12 }] };

function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.8, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 8; d[o + 1] = 6; d[o + 2] = 20; d[o + 3] = Math.round(q * 0.4 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUD_HI_C, CLOUD_MID_C, CLOUD_LO_C, BUSH_C,  DH_C, CITY_C, BBF_C, BB_AD, PITCH, WATER_C, SAND_C, RACK_C, TOWER_C, BEACHED_C,
  TOWEL_C, COOLER_C, TRUNK_C, CROWN_C, VIG_C;
function prerender() {
  HL = {
    moon: makeHalo(54, '#e8a88a', 0.18, 1.7), moonCore: makeHalo(20, '#fff0d8', 0.3, 1.5), venus: makeHalo(4, '#fff6e0', 0.5, 1),
    red: makeHalo(4, '#ff3030', 0.55, 1), hsign: makeHalo(16, '#ff7a9a', 0.28), prom: makeHalo(4, '#ffd890', 0.4, 1),
    lh: makeHalo(5, '#fff0c8', 0.45, 1.2), lhWide: makeHalo(22, '#ffe0b0', 0.1, 1.6),
    bbWash: makeHalo(26, '#ffb8a0', 0.08, 1.6), bbLamp: makeHalo(3, '#fff0c8', 0.18, 1.2),
    landing: makeHalo(8, '#ffffff', 0.45, 1.4), mast: makeHalo(4, '#ffffff', 0.5, 1), boat: makeHalo(10, '#ffd08a', 0.3),
    torch: makeHalo(34, '#ff9a40', 0.22, 1.6), pool: makePool(62, 30, '#ff9a50', 0.3), torchCore: makeHalo(9, '#ffd890', 0.5, 1.3),
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  CLOUD_HI_C = renderClouds(CLOUD_HI, W, ['#2a2450', '#3a2c5a', '#4e3462', '#6a3e68'], [16, 44]);
  CLOUD_MID_C = renderClouds(CLOUD_MID, W, ['#2e2652', '#40305c', '#583a66', '#7a4a6c'], [62, 98]);
  CLOUD_LO_C = renderClouds(CLOUD_LO, 240, ['#4a3060', '#6a3c66', '#94506a', '#c86e6a', '#e89878'], [114, 138]);
  DH_C = renderDiamondHead(); CITY_C = renderCity();
  BBF_C = renderBillboardFrame(); BB_AD = [renderBillboardAd([1, 1, 1]), renderBillboardAd([0, 1, 1])];
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  WATER_C = renderWater(); SAND_C = renderSand();
  RACK_C = renderRack(); TOWER_C = renderTower(); BEACHED_C = renderBeached(); TOWEL_C = renderTowel(); COOLER_C = renderCooler();
  TRUNK_C = renderTrunk(); CROWN_C = Array.from({ length: PALM_NF }, (_, f) => renderCrown(f / (PALM_NF - 1) * 2 - 1));
  BUSH_C = [0, 1, 2].map(renderBushes);
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
const CROWD = 0.7, ACCENT = '#ff8f6b';
const DL = [];                                        // this frame's draw list: [feet row, draw], sorted
const put = (y, draw) => DL.push([y, draw]);
function drawWater(t, billboard, crowd) {
  ctx.drawImage(WATER_C, 0, WATER_TOP);
  for (const m of MIRRORS) reflect(ctx, t, { top: m.top, rows: 38, squash: 1.5, alpha: 0.42, k1: 40, k2: 96, amp: 0.5, grow: 0.05, x: m.x, w: m.w });
  for (const s of WSTREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, farY(s.x) + 1, farY(s.x) + 22);
  if (billboard) for (const s of WSTREAKS_BB) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, farY(s.x) + 1, farY(s.x) + 20);
  streak(ctx, t, LH.x, 2, '#fff0c8', 0.35 * lighthouse(t), 8, HZ + 1, HZ + 26);
  drawAfterglowSea(t);
  drawRipples(t);
  drawMoonPath(t);
  drawReef(t);
  drawLineup(t, crowd);
  drawCatamaran(t, crowd);
  drawOutrigger(t, crowd);
  drawPaddler(t, crowd);
}
function renderFrame(t, { pitch, billboard = true, weather, crowd = CROWD, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    if (s.big) { ctx.globalAlpha = a * 0.35; rect(ctx, s.x - 1, s.y, 3, 1, s.col); rect(ctx, s.x, s.y - 1, 1, 3, s.col); }
    ctx.globalAlpha = a; px(ctx, s.x, s.y, s.col);
  }
  ctx.globalAlpha = 0.9 + 0.1 * wave(t, 30); px(ctx, VENUS.x, VENUS.y, '#fff8e8'); ctx.globalAlpha = 1;
  halo(HL.venus, VENUS.x, VENUS.y, 0.8);
  drawShootingStars(t);
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  drawClouds(t, CLOUD_HI_C, W);
  drawPlane(t);
  ctx.globalAlpha = 0.8; drawClouds(t, CLOUD_MID_C, W); ctx.globalAlpha = 1;
  drawClouds(t, CLOUD_LO_C, 240);
  drawTerns(t);                                       // behind the skyline, so their full crossing clears the hotels
  ctx.drawImage(DH_C.c, 0, 0);
  ctx.globalAlpha = 0.55 + 0.35 * (0.5 + 0.5 * wave(t, 2, 0.1)); ctx.drawImage(DH_C.glow, 0, 0); ctx.globalAlpha = 1;   // the afterglow on its face, breathing
  drawSlope(t);
  drawLighthouse(t);
  ctx.drawImage(CITY_C, 0, 0);
  drawCity(t);
  if (billboard) drawBillboard(t, pitch);
  drawWater(t, billboard, crowd);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  ctx.drawImage(SAND_C, 0, 0);
  drawPools(t);
  drawWaves(t);
  drawCrabs(t);
  DL.length = 0;
  put(RACK.fy, () => ctx.drawImage(RACK_C, RACK.x, RACK.fy - RACK_H + 1));
  put(TOWER.fy, () => ctx.drawImage(TOWER_C, TOWER.x, TOWER.fy - TOWER_H + 1));
  put(BEACHED.fy, () => ctx.drawImage(BEACHED_C, BEACHED.x, BEACHED.fy - 12));
  put(TOWEL.y, () => ctx.drawImage(TOWEL_C, TOWEL.x, TOWEL.y - 3));
  if (crowd > 0) { couple(t, put); ukulele(t, put); }
  else put(UKE.fy, () => ctx.drawImage(COOLER_C, UKE.x - 4, UKE.fy - 6));
  walkers(t, crowd, put);
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, draw] of DL) draw();
  drawTorches(t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawPalm(t);
  drawMoths(t);
  ctx.drawImage(BUSH_C[Math.round(1 + 0.75 * wave(t, 40) + 0.35 * wave(t, 97, 0.3))] || BUSH_C[1], 0, 0);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'honolulu',
  name: 'Honolulu',
  tagline: 'Waikiki dusk by Diamond Head',
  timeZone: 'Pacific/Honolulu',
  accent: '#ff8f6b',                                 // coral, the afterglow (ACCENT)
  focusX: DH.sx,
  landmark: [158, DH.x1],                              // Diamond Head from where it rises past the towers to its point
  landmarkTop: DH.top,                                 // the summit's row, which landscape crops keep
  billboard: BB,
  crowd: CROWD,
  ambience: { waves: 0.75, wind: 0.25, crickets: 0.2, crowd: 0.06 },
  events: [
    { t: 48.2, type: 'splash', x: 216, gain: 0.5 }, { t: 168.2, type: 'splash', x: 216, gain: 0.5 },
    { t: CAT.t0, type: 'boat', dur: CAT.t1 - CAT.t0, pan: [1, -1], gain: 0.35 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
